import { useState, createContext, useContext, ReactNode, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import {
  getAccessToken,
  getRefreshToken,
  getUserInfo,
} from "@/lib/storeManager";
import ServiceDeskAPIService from "@sd/services/APIs";
import type { SdAppRole } from "@sd/lib/sdPaths";
import {
  SD_ROLE_STORAGE_KEY,
  clearSdAuthSession,
  hasRefreshToken,
  isValidAccessToken,
  notifySdAuthChanged,
  assertSecureAuthStorage,
  redirectToSdLogin,
  tryRefreshAccessToken,
  verifySecureAuthStorage,
} from "@sd/lib/sdAuthSession";
import {
  getSecureLocalItem,
  rebindSecureRole,
  removeSecureLocalItem,
} from "@/lib/secureLocalStorage";
import { clearAllPersistedTicketToolbarFilters } from "@sd/lib/ticketListFilterStorage";

/** Auto-refresh the access token every 14 minutes (access token life is ~15 min). */
const TOKEN_REFRESH_INTERVAL_MS = 14 * 60 * 1000;

export type AppRole = SdAppRole;
interface SDRoleItem {
  role_id: string;
  role_name: AppRole;
}

interface AuthContextType {
  role: AppRole | null;
  isLoading: boolean;
  isSuperAdmin: boolean;    // ServiceDesk_Admin
  isSupportAgent: boolean;  // ServiceDesk_Agent | ServiceDesk_SuperAgent
  isEndUser: boolean;       // ServiceDesk_User
  isSupervisor: boolean;    // alias for Admin
  /** Pehle `POST /auth/logout/` (refresh ke saath), phir local clear */
  signOut: () => Promise<void>;
  refetchRole: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** user_id API ke liye — userInfo mein id ho ya JWT mein user_id */
export function getServiceDeskUserId(): string | null {
  const info = getUserInfo() as
    | (Record<string, unknown> & { id?: string; user_id?: string })
    | null;
  if (info?.id) return String(info.id);
  if (info?.user_id) return String(info.user_id);
  const token = getAccessToken();
  if (!token) return null;
  try {
    const p = jwtDecode<Record<string, unknown>>(token);
    const uid = p.user_id ?? p.userId;
    if (typeof uid === "string") return uid;
  } catch {
    return null;
  }
  return null;
}

async function ensureValidAccessToken(): Promise<boolean> {
  if (isValidAccessToken()) return true;
  if (!hasRefreshToken()) return false;
  return tryRefreshAccessToken();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [role, setRoleState] = useState<AppRole | null>(() => {
    if (typeof window === "undefined") return null;
    if (!verifySecureAuthStorage()) return null;
    if (!isValidAccessToken() && !hasRefreshToken()) {
      removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
      return null;
    }
    return (getSecureLocalItem(SD_ROLE_STORAGE_KEY) as AppRole) || null;
  });
  const [isLoading, setIsLoading] = useState(
    () => isValidAccessToken() || hasRefreshToken()
  );

  const fetchRole = useCallback(async (options?: { silent?: boolean }) => {
    // A silent refresh (e.g. background token refresh) must not toggle the
    // global loading state — otherwise loading-gated screens unmount and any
    // in-progress ticket form loses its content.
    const silent = options?.silent ?? false;
    const sessionOk = await ensureValidAccessToken();
    if (!sessionOk || !assertSecureAuthStorage()) {
      setRoleState(null);
      removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
      setIsLoading(false);
      return;
    }

    const userId = getServiceDeskUserId();
    if (!userId) {
      setIsLoading(false);
      return;
    }

    if (!silent) setIsLoading(true);
    try {
      const response = await ServiceDeskAPIService.auth.getCurrentRole(userId);
      const roles: SDRoleItem[] = response.data?.roles ?? [];

      // Priority: Admin > SuperAgent > Agent > User
      const resolved =
        roles.find(r => r.role_name === "ServiceDesk_Admin")?.role_name ||
        roles.find(r => r.role_name === "ServiceDesk_SuperAgent")?.role_name ||
        roles.find(r => r.role_name === "ServiceDesk_Agent")?.role_name ||
        roles.find(r => r.role_name === "ServiceDesk_User")?.role_name ||
        null;

      setRoleState(resolved);
      if (resolved) rebindSecureRole(resolved);
      else removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
    } catch {
      // Network / transient errors — keep cached role if tokens are still valid
      if (!isValidAccessToken() && !(await tryRefreshAccessToken())) {
        setRoleState(null);
        removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
      } else if (!assertSecureAuthStorage()) {
        setRoleState(null);
        removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRole();
  }, [fetchRole]);

  useEffect(() => {
    const onAuthChanged = () => {
      void fetchRole({ silent: true });
    };
    window.addEventListener("sd-auth-changed", onAuthChanged);
    return () => window.removeEventListener("sd-auth-changed", onAuthChanged);
  }, [fetchRole]);

  // Every 14 minutes, send the current refresh token to /auth/token-refresh/
  // and store the new access + refresh tokens that come back. This keeps the
  // session alive so the page never lapses / logs out while the user is
  // creating or editing a ticket. (tryRefreshAccessToken handles sending the
  // current refresh token and persisting the new access + refresh tokens.)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!hasRefreshToken()) return;
      void tryRefreshAccessToken();
    }, TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Detect DevTools / manual localStorage edits while app is open.
  useEffect(() => {
    const verifyStorage = () => {
      if (!assertSecureAuthStorage()) {
        setRoleState(null);
        redirectToSdLogin();
      }
    };

    window.addEventListener("focus", verifyStorage);
    const interval = setInterval(verifyStorage, 30_000);
    return () => {
      window.removeEventListener("focus", verifyStorage);
      clearInterval(interval);
    };
  }, []);

  const signOut = useCallback(async () => {
    const refresh = getRefreshToken();
    if (refresh) {
      try {
        await ServiceDeskAPIService.auth.logout(refresh);
      } catch {
        // Clear local session on 4xx/5xx errors too
      }
    }
    setRoleState(null);
    clearSdAuthSession();
    clearAllPersistedTicketToolbarFilters();
    queryClient.clear();
    notifySdAuthChanged();
  }, [queryClient]);

  const value: AuthContextType = {
    role,
    isLoading,
    isSuperAdmin:   role === 'ServiceDesk_Admin',
    isSupportAgent: role === 'ServiceDesk_Agent' || role === 'ServiceDesk_SuperAgent',
    isEndUser:      role === 'ServiceDesk_User',
    isSupervisor:   role === 'ServiceDesk_Admin',
    signOut,
    refetchRole: fetchRole,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
